import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Bot,
  PlusCircle,
  User,
  HelpCircle,
  LogOut,
  ChevronRight,
  ChevronLeft,
  MessageSquarePlus,
  Sparkles,
  PanelLeftClose,
  PanelLeft,
} from "lucide-react";
import { useApp } from "../context/AppContext";
import Logo from "./Logo";
import Dialog from "./Dialog";

export default function DashboardSidebar() {
  const navigate = useNavigate();
  const { signOut } = useApp();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const handleSignOut = async () => {
    setIsLoggingOut(true);
    await signOut();
    setIsLoggingOut(false);
    setShowLogoutDialog(false);
    navigate("/");
  };

  const navItems = [
    { to: "/dashboard", icon: Bot, label: "My Agents", end: true },
    { to: "/create", icon: PlusCircle, label: "Create Agent" },
  ];

  const bottomItems = [
    { to: "/dashboard/profile", icon: User, label: "Profile" },
    { to: "/dashboard/help", icon: HelpCircle, label: "Help & Support" },
  ];

  return (
    <aside
      className={`${
        isCollapsed ? "w-20" : "w-64"
      } bg-white border-r border-border flex flex-col h-screen sticky top-0 transition-all duration-300 ease-in-out`}
    >
      {/* Logo & Toggle */}
      <div className={`p-4 border-b border-border flex items-center ${isCollapsed ? "justify-center" : "justify-between"}`}>
        {!isCollapsed && <Logo />}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="p-2 rounded-lg hover:bg-primary/10 text-primary hover:text-primary transition-colors"
          title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? (
            <PanelLeft className="w-5 h-5" />
          ) : (
            <PanelLeftClose className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* Scrollable region: nav, promo cards, and bottom section all live in one
          flex column so the sign-out stays reachable on short viewports. */}
      <div className="flex-1 overflow-y-auto flex flex-col min-h-0">
      {/* Navigation */}
      <nav className={`${isCollapsed ? "p-2" : "p-4"} space-y-1`}>
        {navItems.map(({ to, icon: Icon, label, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            title={isCollapsed ? label : undefined}
            className={({ isActive }) => `
              sidebar-link
              ${isActive ? "active" : ""}
              ${isCollapsed ? "justify-center px-3" : ""}
            `}
          >
            <Icon className="w-5 h-5 flex-shrink-0" />
            {!isCollapsed && <span>{label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* Request Custom Agent Card - Hidden when collapsed */}
      {!isCollapsed && (
        <div className="p-4 pb-2">
          <div className="p-4 bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-xl border border-amber-500/20">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-amber-600" />
              <span className="text-sm font-semibold text-amber-700">
                Custom Agent
              </span>
            </div>
            <p className="text-xs text-text-secondary mb-3">
              Need a specialized AI agent? Let us build it for you.
            </p>
            <NavLink
              to="/dashboard/custom-agent"
              className="block w-full text-center px-3 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity"
            >
              Request Custom Agent
            </NavLink>
          </div>
        </div>
      )}

      {/* Collapsed: Custom Agent Icon Button */}
      {isCollapsed && (
        <div className="p-2">
          <NavLink
            to="/dashboard/custom-agent"
            title="Request Custom Agent"
            className="flex items-center justify-center p-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:opacity-90 transition-opacity"
          >
            <Sparkles className="w-5 h-5" />
          </NavLink>
        </div>
      )}

      {/* Beta Feedback Card - Hidden when collapsed */}
      {!isCollapsed && (
        <div className="p-4 pt-2">
          <div className="p-4 bg-gradient-to-br from-primary/10 to-accent/10 rounded-xl border border-primary/20">
            <div className="flex items-center gap-2 mb-2">
              <MessageSquarePlus className="w-5 h-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                Beta Feedback
              </span>
            </div>
            <p className="text-xs text-text-secondary mb-3">
              Help us improve! Share your thoughts and suggestions.
            </p>
            <NavLink
              to="/dashboard/feedback"
              className="block w-full text-center px-3 py-2 bg-gradient-to-r from-primary to-accent text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity"
            >
              Give Feedback
            </NavLink>
          </div>
        </div>
      )}

      {/* Collapsed: Feedback Icon Button */}
      {isCollapsed && (
        <div className="p-2">
          <NavLink
            to="/dashboard/feedback"
            title="Give Feedback"
            className="flex items-center justify-center p-3 bg-gradient-to-r from-primary to-accent text-white rounded-lg hover:opacity-90 transition-opacity"
          >
            <MessageSquarePlus className="w-5 h-5" />
          </NavLink>
        </div>
      )}

      {/* Bottom section — mt-auto pins it to the bottom on tall viewports
          but lets it flow naturally inside the scroll on short ones. */}
      <div className={`mt-auto ${isCollapsed ? "p-2" : "p-4"} border-t border-border space-y-1`}>
        {bottomItems.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            title={isCollapsed ? label : undefined}
            className={({ isActive }) => `
              sidebar-link
              ${isActive ? "active" : ""}
              ${isCollapsed ? "justify-center px-3" : ""}
            `}
          >
            <Icon className="w-5 h-5 flex-shrink-0" />
            {!isCollapsed && <span>{label}</span>}
          </NavLink>
        ))}
        <button
          onClick={() => setShowLogoutDialog(true)}
          title={isCollapsed ? "Sign Out" : undefined}
          className={`sidebar-link w-full text-red-500 hover:bg-red-50 hover:text-red-600 ${
            isCollapsed ? "justify-center px-3" : "text-left"
          }`}
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          {!isCollapsed && <span>Sign Out</span>}
        </button>
      </div>
      </div>
      {/* end scrollable region */}

      {/* Logout Confirmation Dialog */}
      <Dialog
        isOpen={showLogoutDialog}
        onClose={() => setShowLogoutDialog(false)}
        onConfirm={handleSignOut}
        title="Sign Out"
        message="Are you sure you want to sign out? You'll need to sign in again to access your agents."
        confirmText="Sign Out"
        cancelText="Cancel"
        variant="danger"
        isLoading={isLoggingOut}
      />
    </aside>
  );
}

// Skeleton component for loading stats
function StatsSkeleton() {
  return (
    <div className="mt-4 pt-4 border-t border-border grid grid-cols-3 gap-4 animate-pulse">
      <div>
        <div className="h-6 w-8 bg-gray-200 rounded mb-1" />
        <p className="text-xs text-text-muted">Conversations</p>
      </div>
      <div>
        <div className="h-6 w-8 bg-gray-200 rounded mb-1" />
        <p className="text-xs text-text-muted">Leads</p>
      </div>
      <div>
        <div className="h-6 w-8 bg-gray-200 rounded mb-1" />
        <p className="text-xs text-text-muted">Messages</p>
      </div>
    </div>
  );
}

export function AgentCard({ agent, onClick, loadingStats = false }) {
  // Handle stats safely - backend may not provide stats object
  const stats = agent.stats || {};
  const isLive = agent.status === 'active' || agent.status === 'live';
  const isWhatsAppConnected = agent.whatsappConnected || agent.whatsapp_status === 'connected';

  return (
    <div onClick={onClick} className="card card-hover p-5 cursor-pointer group">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
            <Bot className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-text-primary group-hover:text-primary transition-colors">
              {agent.name}
            </h3>
            <p className="text-sm text-text-muted">{agent.business_type || agent.businessType || 'AI Agent'}</p>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-text-muted group-hover:text-primary group-hover:translate-x-1 transition-all" />
      </div>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        {/* Status badge */}
        <span
          className={`badge ${
            isLive ? "badge-success" : "badge-warning"
          }`}
        >
          <span
            className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
              isLive ? "bg-success" : "bg-amber-500"
            }`}
          />
          {isLive ? "Live" : "Paused"}
        </span>

        {/* WhatsApp badge */}
        {isWhatsAppConnected && (
          <span className="badge badge-success">
            <svg
              className="w-3 h-3 mr-1.5"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            Connected
          </span>
        )}
      </div>

      {/* Stats - show skeleton when loading */}
      {loadingStats ? (
        <StatsSkeleton />
      ) : (
        <div className="mt-4 pt-4 border-t border-border grid grid-cols-3 gap-4">
          <div>
            <p className="text-lg font-semibold text-text-primary">
              {stats.totalConversations || stats.total_conversations || 0}
            </p>
            <p className="text-xs text-text-muted">Conversations</p>
          </div>
          <div>
            <p className="text-lg font-semibold text-text-primary">
              {stats.leadsCollected || stats.leads_collected || 0}
            </p>
            <p className="text-xs text-text-muted">Leads</p>
          </div>
          <div>
            <p className="text-lg font-semibold text-text-primary">
              {stats.messagesHandled || stats.messages_handled || 0}
            </p>
            <p className="text-xs text-text-muted">Messages</p>
          </div>
        </div>
      )}
    </div>
  );
}
